import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deletePackagingPurchaseOrder,
  getPackagingPurchaseOrders,
  removePackagingPurchaseOrder,
} from 'src/redux/slices/packaging-purchase-order';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IPackagingPurchaseOrderListItem,
  IPackagingPurchaseOrderTableFilters,
  IPackagingPurchaseOrderTableFilterValue,
  IPackagingPurchaseOrderTableSort,
} from 'src/types/packaging-purchase-order';

import PackagingPurchaseOrderTableRow from '../packaging-purchase-order-table-row';
import PackagingPurchaseOrderTableToolbar from '../packaging-purchase-order-table-toolbar';

const TABLE_HEAD = [
  { id: 'orderCode', label: 'Order #', align: 'left' },
  { id: 'purchaseDate', label: 'Purchase Date', align: 'left' },
  { id: 'supplier', label: 'Supplier', align: 'left' },
  { id: 'totalAfterDiscount', label: 'Total', align: 'right' },
  { id: 'updatedAt', label: 'Updated', align: 'left' },
  { id: '' },
];

const defaultFilters: IPackagingPurchaseOrderTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

// Sorted by createdAt (not updatedAt) so the top row on an unfiltered first
// page always matches the backend's "most recently created" rule — that's
// the only row edit/delete is enabled for.
const defaultSortBy: IPackagingPurchaseOrderTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function PackagingPurchaseOrderListView() {
  const { status, orders, error } = useAppSelector((state) => state.packagingPurchaseOrder);

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  // Edit/delete is only meaningful when viewing the true, unfiltered,
  // createdAt-desc-sorted first page — otherwise which row is "the last
  // order" can't be determined client-side.
  const canDetermineLastOrder =
    page === 0 &&
    sort.sortBy === 'createdAt' &&
    sort.order === 'desc' &&
    !filters.generalSearch &&
    !filters.startDate &&
    !filters.endDate;

  const handleFilters = useCallback(
    (name: string, value: IPackagingPurchaseOrderTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.packagingPurchaseOrder.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedOrders = useCallback(() => {
    dispatch(
      getPackagingPurchaseOrders({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (order: IPackagingPurchaseOrderListItem, index: number) => {
      dispatch(removePackagingPurchaseOrder({ orderId: order._id }));

      try {
        await dispatch(deletePackagingPurchaseOrder({ order, index })).unwrap();

        fetchPaginatedOrders();
        enqueueSnackbar('Purchase order deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedOrders]
  );

  useEffect(() => {
    fetchPaginatedOrders();
  }, [fetchPaginatedOrders]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Packaging Purchase Orders"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_PACKAGING_PURCHASE_ORDER,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.packagingPurchaseOrder.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Purchase Order
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <PackagingPurchaseOrderTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={orders.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {orders?.items.map((row: IPackagingPurchaseOrderListItem, index: number) => (
                      <PackagingPurchaseOrderTableRow
                        key={row._id}
                        row={row}
                        isEditable={canDetermineLastOrder && index === 0}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && orders.count === 0 && (
                  <TableNoData
                    message="No purchase orders found"
                    notFound={orders.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={orders.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
